import Moment from 'moment';
import { VatTariffs, VatService } from '../VatService';
import Resource from './Resource';

/**
 * Invoice Resource
 */
class Invoice extends Resource
{
    constructor() {
        super('accounting.invoice', ['invoices']);
    }

    search(query, params = {}, config = {}) {
      params = Object.assign(params, { query });

      return this._doGet(['search'], params, config);
    }

    refund(id, cancellation_reason, cancellation_description = null) {
        return this._doPost([id, 'refund'], { cancellation_reason, cancellation_description });
    }

    void(id, cancellation_reason, cancellation_description = null) {
        return this._doPost([id, 'void'], { cancellation_reason, cancellation_description });
    }

    options() {
        return this._doGet(['options']);
    }
}   

export let InvoiceResource = new Invoice();

/**
 * Invoice Item Resource
 */
class InvoiceItem extends Resource
{
    constructor() {
        super('accounting.invoice_item', ['invoices/items']);
    }
    options() {
        return this._doGet(['options']);
    }
}   

export let InvoiceItemResource = new InvoiceItem();


export let InvoiceSchema = function(defaults = {}) {
    let schema = {
        customer_reference: null,
        tax_at: (new Moment()).toDate(),
        refunded_at: null,
        voided_at: null,
        contact_id: null,
        contact: null,
        team_id: null,
        team: null,
        items: [],
        payments: [],
        id: null,
        is_posted: false,
        cancellation_reason: '',
        cancellation_description: '',
        notes: '',
        status_text: 'Draft',
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
}

/**
 * Quote Resource
 */
class Quote extends Resource
{
    constructor() {
        super('accounting.quote', ['quotes']);
    }

    options() {
        return this._doGet(['options']);
    }
}   

export let QuoteResource = new Quote();


export let QuoteSchema = function(defaults = {}) {
    let schema = {
        customer_reference: null,
        tax_at: (new Moment()).toDate(),
        contact_id: null,
        team_id: null,
        team: null,
        items: [],
        id: null
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
}

export let InvoiceItemTypes = {
    TYPE_VEHICLE: 'vehicle', 
    TYPE_DISCOUNT: 'discount', 
    TYPE_LABOUR: 'labour', 
    TYPE_PRODUCT: 'product', 
    TYPE_PART_EXCHANGE_SETTLEMENT: 'part_exchange_settlement',
    TYPE_WARRANTY: 'warranty', 
    TYPE_EXEMPT_LINKED_WARRANTY: 'linked_warranty', 
    TYPE_GAP_RTI_INSURANCE: 'gap_rti',
    TYPE_SERVICE_PLAN: 'service_plan', 
    TYPE_SUNDRY_ITEM: 'sundry_item', 
    TYPE_OTHER: 'other',
    TYPE_PART: 'part',
    TYPE_SERVICE: 'service',
    TYPE_COURTESY_CAR_SURCHARGE: 'courtesy_car_surcharge',
    TYPE_CONSUMABLE: 'consumable',
    TYPE_DISBURSEMENT: 'disbursement',
    TYPE_KIT: 'kit',
};

export let InvoiceStatuses = {
    PENDING: 'pending',
    BALANCE_DUE: 'due',
    PAID: 'paid',
    REFUNDED: 'refunded',
    VOID: 'void',
    CASHBACK: 'cashback',
}

export let InvoiceCancellationReasons = [
    { 
        value: 'problem.vehicle.mechanical', 
        label: 'The vehicle has a mechanical fault',
        sentance: 'the vehicle having a mechanical fault',
    },
    { 
        value: 'problem.vehicle.expectations', 
        label: 'The vehicle does not meet expectations',
        sentance: 'the vehicle not meeting expectations',
    },
    { 
        value: 'problem.vehicle.purchased', 
        label: 'The vehicle being purchased elsewhere',
        sentance: 'the customer purchasing a vehicle from elsewhere'
    },
    { 
        value: 'other', 
        label: 'Other reason',
        sentance: 'the reason described below..'
    }
]

export let InvoiceItemTypeList = [
    {
        label: 'Part',
        value: InvoiceItemTypes.TYPE_PART,
    },{
        label: 'Consumable',
        value: InvoiceItemTypes.TYPE_CONSUMABLE,
    },{
        label: 'Service',
        value: InvoiceItemTypes.TYPE_SERVICE,
    },{
        label: 'Labour',
        value: InvoiceItemTypes.TYPE_LABOUR,
    },{
        label: 'Discount',
        value: InvoiceItemTypes.TYPE_DISCOUNT,
    },{
        label: 'Vehicle',
        value: InvoiceItemTypes.TYPE_VEHICLE,
    },{
        label: 'Product',
        value: InvoiceItemTypes.TYPE_PRODUCT,
    },{
        label: 'Part Exchange Settlement',
        value: InvoiceItemTypes.TYPE_PART_EXCHANGE_SETTLEMENT,
    },{
        label: 'In-House Warranty',
        value: InvoiceItemTypes.TYPE_WARRANTY,
    },{
        label: 'Linked Warranty',
        value: InvoiceItemTypes.TYPE_EXEMPT_LINKED_WARRANTY,
    },{
        label: 'GAP / RTI',
        value: InvoiceItemTypes.TYPE_GAP_RTI_INSURANCE
    },{
        label: 'Service Plan',
        value: InvoiceItemTypes.TYPE_SERVICE_PLAN,
    },{
        label: 'Sundry Item',
        value: InvoiceItemTypes.TYPE_SUNDRY_ITEM,
    },{
        label: 'Courtesy Car Surcharge',
        value: InvoiceItemTypes.TYPE_COURTESY_CAR_SURCHARGE,
    },{
        label: 'Disbursement',
        value: InvoiceItemTypes.TYPE_DISBURSEMENT,
    },{
        label: 'Other',
        value: InvoiceItemTypes.TYPE_OTHER,
    }
];

export let InvoiceItemSchema = function(defaults = {}) {
    let schema = {
        type: InvoiceItemTypes.TYPE_PRODUCT,
        sku: null,
        invoice_id: '',
        task_id: '',
        instance_id: null,
        description: '',
        unit_cost: 0.00,
        vat_rate: VatTariffs.STANDARD,
        quantity: 1
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
}

export let QuoteItemSchema = function(defaults = {}) {
    let schema = {
        type: InvoiceItemTypes.TYPE_PRODUCT,
        sku: null,
        quote_id: '',
        description: '',
        unit_cost: 0.00,
        vat_rate: VatTariffs.STANDARD,
        quantity: 1,
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
}

export class InvoiceHelper {
    constructor(invoice) {
        this.invoice = invoice;
        this.vatService = new VatService(this.invoice.tax_at)
    }

    getVatService() {
        return this.vatService;
    }

    itemNet(i) {
        return _.round(this.invoice.items[i].unit_cost * this.invoice.items[i].quantity, 2);
    }

    itemVat(i) {
        return this.invoice.items[i].custom_total_vat > 0 ?
            this.invoice.items[i].custom_total_vat :
            this.vatService.getVatFor(this.itemNet(i), this.invoice.items[i].vat_rate);
    }

    itemTotal(i) {
        return parseFloat(this.itemNet(i)) + parseFloat(this.itemVat(i));
    }

    vatRate(tariff = 'S') {
        return this.vatService.getTariff(tariff)
    }

    currentItems() {
        return this.invoice.items.filter(item => !item.is_deleted)
    }

    net() {
        let total = 0;

        for (let i in this.currentItems()) {
            let net = Number.parseFloat(this.currentItems()[i].unit_cost).toFixed(2) * this.currentItems()[i].quantity
            total += Number.parseFloat((Math.round(net * 100)/100).toFixed(2));
        }

        return total;
    }

    netVatable() {
        let total = 0;

        for (let i in this.currentItems()) {
            let net = this.currentItems()[i].unit_cost * this.currentItems()[i].quantity;
            if (this.vatService.getVatFor(net, this.currentItems()[i].vat_rate) > 0) {
                total += this.currentItems()[i].unit_cost * this.currentItems()[i].quantity;
            }
        }

        return _.round(total, 2);
    }

    vat() {
        let total = 0;

        for (let i in this.currentItems()) {
            if (this.currentItems()[i].custom_total_vat) {
                total += parseFloat(this.currentItems()[i].custom_total_vat);
            } else {
                let net = this.currentItems()[i].unit_cost * this.currentItems()[i].quantity;
                total += this.vatService.getVatFor(net, this.currentItems()[i].vat_rate);
            }
        }

        return _.round(total, 2);
    }

    total() {
        return this.net() + this.vat()
    }

    totalExcludingSettlements() {
        let total = this.total();

        for (let i in this.currentItems()) {
            if (InvoiceItemTypes.TYPE_PART_EXCHANGE_SETTLEMENT === this.currentItems()[i].type) {
                let net = this.currentItems()[i].unit_cost * this.currentItems()[i].quantity;
                total -= this.vatService.vatify(net, this.currentItems()[i].vat_rate);
            }
        }

        return _.round(total, 2);
    }

    balanceDue() {
        if (!this.invoice.payments) {
            throw new Error('The invoices payments are not loaded, a balance cannot be calculated');
        }

        let paymentTotal = _.sumBy(this.invoice.payments, item => {
            return item.is_posted ? parseFloat(item.amount) : 0
        })

        return _.round(this.total() - paymentTotal, 2)
    }
}